import type { UseFormReturn } from 'react-hook-form'
import {
  TRIGGER_TYPE_LABELS,
  ACTION_TYPE_LABELS,
  CATEGORY_OPTIONS,
  type StepRulesData,
  type StepTriggerData,
  type StepActionData,
} from '../types'

interface StepRulesFormProps {
  form: UseFormReturn<StepRulesData>
  triggerData: StepTriggerData | null
  actionData: StepActionData | null
  onSubmit: () => void
  onBack: () => void
  isSubmitting: boolean
  submitLabel: string
}

export function StepRulesForm({
  form,
  triggerData,
  actionData,
  onSubmit,
  onBack,
  isSubmitting,
  submitLabel,
}: StepRulesFormProps) {
  const isCategoryReachedTrigger = triggerData?.trigger_type === 'category_reached'
  const watchedValidDaysUnlimited = form.watch('valid_days_unlimited')
  const watchedMaxYearUnlimited = form.watch('max_grants_per_year_unlimited')
  const watchedMaxMonthUnlimited = form.watch('max_grants_per_month_unlimited')
  const watchedMinPurchaseNone = form.watch('min_purchase_none')
  const watchedMaxDiscountNone = form.watch('max_discount_none')
  const watchedCooldownNone = form.watch('cooldown_days_none')

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Eligible categories */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Categorías elegibles *</label>
        {isCategoryReachedTrigger ? (
          <div>
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium capitalize">
              {triggerData?.trigger_category}
            </span>
            <p className="text-xs text-gray-400 mt-1">
              Determinado automáticamente por el gatillo &quot;Categoría alcanzada&quot;
            </p>
          </div>
        ) : (
          <>
            <div className="flex gap-4">
              {CATEGORY_OPTIONS.map((cat) => (
                <label key={cat} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    value={cat}
                    checked={form.watch('eligible_categories')?.includes(cat) ?? false}
                    onChange={(e) => {
                      const current = form.getValues('eligible_categories') ?? []
                      const next = e.target.checked
                        ? [...current, cat]
                        : current.filter((c) => c !== cat)
                      form.setValue('eligible_categories', next, { shouldValidate: true })
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700 capitalize">{cat}</span>
                </label>
              ))}
            </div>
            {form.formState.errors.eligible_categories && (
              <p className="text-red-500 text-sm mt-1">{form.formState.errors.eligible_categories.message}</p>
            )}
          </>
        )}
      </div>

      {/* Single use */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Uso único</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.watch('single_use') === true}
              onChange={() => form.setValue('single_use', true)}
              className="border-gray-300"
            />
            <span className="text-sm text-gray-700">Sí</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.watch('single_use') === false}
              onChange={() => form.setValue('single_use', false)}
              className="border-gray-300"
            />
            <span className="text-sm text-gray-700">No</span>
          </label>
        </div>
      </div>

      {/* Valid days */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Días de validez</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            {...form.register('valid_days')}
            className="input w-32"
            min={1}
            disabled={watchedValidDaysUnlimited}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...form.register('valid_days_unlimited')}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Sin límite</span>
          </label>
        </div>
      </div>

      {/* Max grants per year */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Max. otorgamientos por año</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            {...form.register('max_grants_per_year')}
            className="input w-32"
            min={1}
            disabled={watchedMaxYearUnlimited}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...form.register('max_grants_per_year_unlimited')}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Sin límite</span>
          </label>
        </div>
      </div>

      {/* Max grants per month */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Max. otorgamientos por mes</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            {...form.register('max_grants_per_month')}
            className="input w-32"
            min={1}
            disabled={watchedMaxMonthUnlimited}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...form.register('max_grants_per_month_unlimited')}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Sin límite</span>
          </label>
        </div>
      </div>

      {/* Min purchase */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Compra mínima ($)</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            {...form.register('min_purchase')}
            className="input w-40"
            min={0}
            disabled={watchedMinPurchaseNone}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...form.register('min_purchase_none')}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Sin mínimo</span>
          </label>
        </div>
      </div>

      {/* Max discount */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Descuento máximo ($)</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            {...form.register('max_discount')}
            className="input w-40"
            min={0}
            disabled={watchedMaxDiscountNone}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...form.register('max_discount_none')}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Sin tope</span>
          </label>
        </div>
      </div>

      {/* Valid channels */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Canales válidos *</label>
        <div className="flex gap-4">
          {['web', 'store'].map((ch) => (
            <label key={ch} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                value={ch}
                checked={form.watch('valid_channels')?.includes(ch) ?? false}
                onChange={(e) => {
                  const current = form.getValues('valid_channels') ?? []
                  const next = e.target.checked
                    ? [...current, ch]
                    : current.filter((c) => c !== ch)
                  form.setValue('valid_channels', next, { shouldValidate: true })
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700 capitalize">{ch === 'web' ? 'Web' : 'Tienda'}</span>
            </label>
          ))}
        </div>
        {form.formState.errors.valid_channels && (
          <p className="text-red-500 text-sm mt-1">{form.formState.errors.valid_channels.message}</p>
        )}
      </div>

      {/* Non-combinable */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">No combinable con otros beneficios</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.watch('non_combinable') === true}
              onChange={() => form.setValue('non_combinable', true)}
              className="border-gray-300"
            />
            <span className="text-sm text-gray-700">Sí</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.watch('non_combinable') === false}
              onChange={() => form.setValue('non_combinable', false)}
              className="border-gray-300"
            />
            <span className="text-sm text-gray-700">No</span>
          </label>
        </div>
      </div>

      {/* Requires email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Requiere email del cliente</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.watch('requires_email') === true}
              onChange={() => form.setValue('requires_email', true)}
              className="border-gray-300"
            />
            <span className="text-sm text-gray-700">Sí</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.watch('requires_email') === false}
              onChange={() => form.setValue('requires_email', false)}
              className="border-gray-300"
            />
            <span className="text-sm text-gray-700">No</span>
          </label>
        </div>
      </div>

      {/* Cooldown days */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Días de enfriamiento entre otorgamientos</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            {...form.register('cooldown_days')}
            className="input w-32"
            min={1}
            disabled={watchedCooldownNone}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...form.register('cooldown_days_none')}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Sin enfriamiento</span>
          </label>
        </div>
      </div>

      {/* Valid until first purchase */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Caduca al realizar primera compra</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.watch('valid_until_first_purchase') === true}
              onChange={() => form.setValue('valid_until_first_purchase', true)}
              className="border-gray-300"
            />
            <span className="text-sm text-gray-700">Sí</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.watch('valid_until_first_purchase') === false}
              onChange={() => form.setValue('valid_until_first_purchase', false)}
              className="border-gray-300"
            />
            <span className="text-sm text-gray-700">No</span>
          </label>
        </div>
      </div>

      {/* Priority */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
        <input type="number" {...form.register('priority')} className="input w-32" min={0} />
        <p className="text-xs text-gray-400 mt-1">Menor número = mayor prioridad</p>
      </div>

      {/* Summary preview */}
      {triggerData && actionData && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-800 mb-2">Resumen</h3>
          <div className="text-sm text-blue-700 space-y-1">
            <p><span className="font-medium">Nombre:</span> {triggerData.name}</p>
            <p><span className="font-medium">Gatillo:</span> {TRIGGER_TYPE_LABELS[triggerData.trigger_type]}</p>
            <p><span className="font-medium">Acción:</span> {ACTION_TYPE_LABELS[actionData.action_type]}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button type="button" onClick={onBack} className="btn btn-secondary">
          Anterior
        </button>
        <button type="submit" disabled={isSubmitting} className="btn btn-primary">
          {isSubmitting ? 'Guardando...' : submitLabel}
        </button>
      </div>
    </form>
  )
}
